'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { redirect } from 'next/navigation';
import { tenant } from '@/interfaces/common';
import { otpFrom } from '@/utils/constants';
import { useGetErrorMessage } from '@/utils/dbMessagesHelper';
import { getDynamicEnvVarsInClient } from '@/utils/envHelper';
import redirectURLChecker from '@/utils/redirectURLChecker';
import { saveLanguagePreference } from '@/utils/saveLanguagePreference';
import { getCookie } from 'cookies-next';
import { toast } from 'sonner';

import { email_template_config } from '@/config/email_template.config';
import postMessageToParent from '@/lib/postMessage';
import useEmailCheck from '@/hooks/useEmailCheck';
import useOtpSend from '@/hooks/useOtpSend';
import useOtpVerify from '@/hooks/useOtpVerify';
import useSessionStorageInfo from '@/hooks/useSessionStorageInfo';
import { useTranslations } from '@/hooks/useTranslations';
import LinkedInButton from '@/components/reUsables/LinkedInButton';

import EmailForm from '../../Login/sections/EmailForm';
import OTPForm from '../../Login/sections/OTPForm';
import OTPSuccess from './OTPSuccess';

//! don't change the message directly depends on auth-module-backend
export const EMAIL_NOT_FOUND = 'User not found, Signup to proceed.';
export const EMAIL_NOT_SIGNEDUP_WITH_TENANT =
  'Email not signed up with this tenant!';

const SignupForm = ({
  tenant,
  secondary_user_id,
  additionalDataObj,
}: {
  tenant: tenant;
  secondary_user_id: string | null;
  additionalDataObj: any;
}) => {
  const { translations, isLoading: isTranslationsLoading } =
    useTranslations('auth_page');

  const { getDBMessage } = useGetErrorMessage();

  const signup_title =
    translations?.signup_title || 'Start Your Design Validation Journey!';
  const verify_email_title =
    translations?.verify_email_title || 'Verify your email address';
  const signup_description =
    translations?.signup_description ||
    'Discover the Ideal IC for Your Design with Ease. Unlock the Power of Real Hardware in the Cloud with LiveBench.';
  const get_started_with_linkedin =
    translations?.get_started_with_linkedin || 'Get started with Linkedin';
  const have_an_account = translations?.have_an_account || 'Have an account?';
  const login = translations?.login || 'Login';

  // Error and success message translations
  const account_exists_message =
    translations?.error_messages?.account_exists ||
    'An account with this email address already exists. Please login';
  const otp_sent_message =
    translations?.success_messages?.otp_sent || 'OTP sent successfully';
  const otp_send_error =
    translations?.error_messages?.otp_send_error ||
    'Error in sending OTP. Please try again later';
  const otp_verify_error =
    translations?.error_messages?.otp_verify_error ||
    'Error in verifying OTP. Please try again later';

  const [isSuccessOTP] = useState(false);
  const [isEmailForm, setIsEmailForm] = useState(true);
  const [email, setEmail] = useState('');
  const [wrongEmail, setWrongEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpErroMsg, setOtpErrorMsg] = useState('');
  const { NEXT_PUBLIC_CW_AUTH_URL } = getDynamicEnvVarsInClient();
  const [checked, setChecked] = useState(false);
  const [isCheckedNewsletter, setIsCheckedNewsletter] = useState(true);
  const currentLanguage = (getCookie('NEXT_LOCALE') as string) || 'en-US';
  const encodedParamsStringify = getCookie('encodedParams') || '{}';
  const encodedParams = JSON.parse(encodedParamsStringify);
  const locale = getCookie('NEXT_LOCALE');
  const { redirecturl, postParent, tenantData, clientId, ssoLink, app_id } =
    useSessionStorageInfo();

  const REQUEST_FROM_SIGNUP = true;
  const { loading, data, error, handleClick } = useEmailCheck({
    REQUEST_FROM_SIGNUP,
  });
  const [errorMsg, setErrorMsg] = useState(error);

  const {
    loading: otpLoading,
    data: otpResponse,
    error: otpError,
    handleClick: sendOtp,
  } = useOtpSend(email_template_config.SIGNUP_OTP_TEMPLATE);

  const {
    loading: otpVerifyLoading,
    data: otpVerifyResponse,
    handleClick: handleOtpVerify,
    error: otpVerifyError,
  } = useOtpVerify();

  const handleVerify = () => {
    handleOtpVerify(
      email,
      tenant?._id,
      otp,
      secondary_user_id,
      isCheckedNewsletter,
      app_id
    );
  };

  useEffect(() => {
    if (email === '') {
      setErrorMsg('');
    }
  }, [email]);

  useEffect(() => {
    if (otpVerifyResponse) {
      if (otpVerifyResponse.success) {
        saveLanguagePreference({
          tenantData,
          accessToken: otpVerifyResponse.accessToken,
          NEXT_PUBLIC_CW_AUTH_URL,
          locale: currentLanguage,
        }).then(() => {
          if (postParent) {
            postMessageToParent({ ...otpVerifyResponse, client_id: clientId });
            redirect('/successful-login');
          }

          if (redirecturl) {
            const newParams = {
              accessToken: otpVerifyResponse.accessToken,
              refreshToken: otpVerifyResponse.refreshToken,
              tenantId: tenantData?._id,
              tenantName: tenantData?.tenantName,
              clientId: clientId,
              new_user: true,
              ...additionalDataObj,
            };

            const updatedURL = redirectURLChecker(redirecturl, {
              ...newParams,
              ...encodedParams,
              locale: locale as string,
            });
            window.location.replace(updatedURL);
          }
        });
      } else {
        toast.error(otpVerifyResponse.message);
        setOtpErrorMsg(otpVerifyResponse.message);
      }
    }
  }, [otpVerifyResponse]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    handleClick(email, tenant?._id);
  };

  useEffect(() => {
    if (data?.isUserInSameTenant) {
      setErrorMsg(account_exists_message);
      toast.error(account_exists_message);
      setIsEmailForm(true);
    } else if (data?.isUserInSameTenant === false) {
      sendOtp(email, tenant?._id, otpFrom.SIGNUP, true, wrongEmail);
    }
  }, [data, account_exists_message]);

  useEffect(() => {
    if (error === EMAIL_NOT_FOUND || error === EMAIL_NOT_SIGNEDUP_WITH_TENANT) {
      setErrorMsg('');
      sendOtp(email, tenant?._id, otpFrom.SIGNUP, true, wrongEmail);
    } else {
      setErrorMsg(getDBMessage(error) || error);
    }
  }, [error]);

  useEffect(() => {
    if (otpResponse?.message) {
      setErrorMsg('');
      setOtpErrorMsg('');
      // Use translated message if available, otherwise use the response message
      toast.success(otp_sent_message || otpResponse.message);
      setIsEmailForm(false);
    }
  }, [otpResponse, otp_sent_message]);

  useEffect(() => {
    if (otpError) {
      const errorMessage = otp_send_error || otpError;
      toast.error(errorMessage);
      setOtpErrorMsg(errorMessage);
    }

    if (otpVerifyError) {
      const errorMessage = otp_verify_error || otpVerifyError;
      toast.error(errorMessage);
      setOtpErrorMsg(errorMessage);
    }
  }, [otpError, otpVerifyError, otp_send_error, otp_verify_error]);

  if (isTranslationsLoading) {
    return (
      <div
        className="flex items-center justify-center overflow-y-auto rounded-3xl bg-white"
        style={{ height: 'calc(100vh - 48px)' }}
      >
        <div className="w-[384px]">
          <div className="h-[53px] w-[120px] animate-pulse rounded bg-gray-200" />
          <div className="mt-12 h-8 w-48 animate-pulse rounded bg-gray-200" />
          <div className="mt-4 h-4 w-64 animate-pulse rounded bg-gray-200" />
          <div className="mt-8 h-10 w-full animate-pulse rounded bg-gray-200" />
          <div className="mt-4 h-10 w-full animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  if (isSuccessOTP) {
    return <OTPSuccess />;
  }

  const handleRevertBackToEmailForm = () => {
    setIsEmailForm(true);
    setWrongEmail(email);
    setEmail('');
  };

  const shouldShowContinueWithSSOButton =
    tenantData?.sso === true ? true : false;

  return (
    <div
      className="flex items-center justify-center overflow-y-auto rounded-3xl bg-white"
      style={{ height: 'calc(100vh - 48px)' }}
    >
      <div className="w-[384px]">
        {tenant?.logo && (
          <Image
            src={tenant?.logo}
            alt={tenant?.tenantName}
            width={120}
            height={53}
          />
        )}

        <h1 className="mb-4 mt-12 text-3xl font-bold">
          {isEmailForm ? signup_title : verify_email_title}
        </h1>
        {isEmailForm && (
          <p className="mb-8 text-sm font-normal text-zinc-500">
            {signup_description}
          </p>
        )}

        {isEmailForm ? (
          <>
            <EmailForm
              loading={loading || otpLoading}
              email={email}
              setEmail={setEmail}
              handleSubmit={handleSubmit}
              fromSignup={true}
              errorMsg={errorMsg}
              checked={checked}
              setChecked={setChecked}
              isCheckedNewsletter={isCheckedNewsletter}
              setIsCheckedNewsletter={setIsCheckedNewsletter}
              shouldShowContinueWithSSOButton={shouldShowContinueWithSSOButton}
              ssoLink={ssoLink}
            />

            {tenant?.linkedin && tenant?._id && (
              <LinkedInButton
                text={get_started_with_linkedin}
                signInPage={false}
                checked={checked}
              />
            )}

            <p className="cursor-pointer pt-6 text-center text-sm text-zinc-500">
              {have_an_account}{' '}
              <Link
                className="font-medium text-primary-950"
                href={`/${tenantData?._id !== undefined ? `?tenantId=${tenantData?._id}` : ''}`}
              >
                {login}
              </Link>
            </p>
          </>
        ) : (
          <OTPForm
            loading={otpVerifyLoading}
            email={email}
            value={otp}
            setValue={setOtp}
            handleVerify={handleVerify}
            setIsEmailForm={setIsEmailForm}
            fromSignup={true}
            tenantId={tenant?._id}
            handleRevertBackToEmailForm={handleRevertBackToEmailForm}
            errorMsg={otpErroMsg}
            setErrorMsg={setOtpErrorMsg}
          />
        )}
      </div>
    </div>
  );
};

export default SignupForm;
